import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/tanstack-react-start";
import { z } from "zod";
import { Input } from "~/components/ui/input";
import { Button, type ButtonProps } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";
import { AlertCircle, Calendar, CheckCircle } from "lucide-react";
import { cn } from "~/lib/cn";
import {
  getNextAvailableSummitOptions,
  checkUserSummitRegistrationOptions,
  registerAuthenticatedUserForSummit,
  registerUnauthenticatedUserForSummit,
  type RegistrationResponse,
} from "~/lib/functions/summit-registration";
import { format, isSameDay } from "date-fns";
import { useAffiliateId } from "~/contexts/affiliate-id";
import { getClerkErrorMessage } from "~/lib/clerk-errors";
import { SUMMIT_SPOT_SECURED_REDIRECT_URL } from "~/lib/shared/constants/summit";
import { trackSummitRegistrationCompleted } from "~/lib/monitoring/track-summit-registration";
import {
  useInboundUtmParameters,
  type InboundUtmParameters,
} from "~/hooks/use-inbound-utm-parameters";
import { mapToUtmRecord } from "~/lib/shared/utm";

const RegistrationFormSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  email: z.string().email("Valid email is required"),
});

type RegistrationForm = z.infer<typeof RegistrationFormSchema>;

type FlowStep = "collect" | "finalizing" | "success";

interface SummitRegistrationProps {
  redirectUrl?: string;
  className?: string;
  inboundUtmParameters?: InboundUtmParameters;
}

const primaryCtaButtonAppearance = {
  className: "w-full font-extrabold uppercase",
  size: "xl",
  variant: "flat-animated",
} satisfies Pick<ButtonProps, "className" | "size" | "variant">;

function formatSummitDateRange(startDate: Date, endDate: Date): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isSameDay(start, end)) {
    return format(start, "MMMM d, yyyy");
  }

  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  ) {
    return `${format(start, "MMMM d")} - ${format(end, "d, yyyy")}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, "MMMM d")} - ${format(end, "MMMM d, yyyy")}`;
  }

  return `${format(start, "MMMM d, yyyy")} - ${format(end, "MMMM d, yyyy")}`;
}

export function SummitRegistration({
  redirectUrl = SUMMIT_SPOT_SECURED_REDIRECT_URL,
  className,
  inboundUtmParameters: inboundUtmParametersProp,
}: SummitRegistrationProps) {
  const { isSignedIn, userId } = useAuth();
  const affiliateId = useAffiliateId();
  const inboundUtmParametersFromHook = useInboundUtmParameters();
  const inboundUtmParameters =
    inboundUtmParametersProp ?? inboundUtmParametersFromHook;
  const queryClient = useQueryClient();
  const utmParametersPayload = useMemo(() => {
    const normalized = mapToUtmRecord(inboundUtmParameters);
    return normalized ?? undefined;
  }, [inboundUtmParameters]);

  const [flowStep, setFlowStep] = useState<FlowStep>("collect");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [finalRedirectUrl, setFinalRedirectUrl] = useState<string | undefined>(
    undefined,
  );

  const enrichRedirectUrl = useCallback(
    (url?: string | null): string | undefined => {
      if (!url) return undefined;

      if (typeof window === "undefined") {
        return url || undefined;
      }

      try {
        const parsed = new URL(url, window.location.origin);

        if (affiliateId && !parsed.searchParams.get("am_id")) {
          parsed.searchParams.set("am_id", affiliateId);
        }

        inboundUtmParameters.forEach((value, key) => {
          if (parsed.searchParams.has(key)) {
            return;
          }

          parsed.searchParams.set(key, value);
        });

        return parsed.toString();
      } catch {
        return url || undefined;
      }
    },
    [affiliateId, inboundUtmParameters],
  );

  const applyTrackingToRedirect = useCallback(
    (url?: string | null) => enrichRedirectUrl(url ?? undefined),
    [enrichRedirectUrl],
  );

  const {
    data: summitData,
    isLoading: summitLoading,
    error: summitError,
  } = useQuery(getNextAvailableSummitOptions);

  const {
    data: registrationState,
    isLoading: registrationLoading,
    error: registrationError,
  } = useQuery({
    ...checkUserSummitRegistrationOptions,
    queryKey: ["summit", "user-registration", userId || "anonymous"],
    enabled: Boolean(isSignedIn && userId),
    retry: false,
  });

  const summit = summitData?.summit ?? null;
  const summitApiError = summitData?.error;
  const registration = registrationState ?? null;

  const form = useForm<RegistrationForm>({
    resolver: zodResolver(RegistrationFormSchema),
    defaultValues: {
      firstName: "",
      email: "",
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const handleServerRegistrationSuccess = useCallback(
    (data: RegistrationResponse) => {
      queryClient.invalidateQueries({
        queryKey: ["summit", "user-registration"],
      });
      setErrorMessage(null);
      setFlowStep("success");
      setIsProcessing(false);
      reset();
      const destination =
        data.redirectUrl ?? redirectUrl ?? SUMMIT_SPOT_SECURED_REDIRECT_URL;
      const sanitizedDestination = enrichRedirectUrl(destination);
      setFinalRedirectUrl(sanitizedDestination ?? destination);

      if (summit) {
        trackSummitRegistrationCompleted({
          summitId: summit.id,
          summitTitle: summit.title,
          registrationType: isSignedIn ? "authenticated" : "unauthenticated",
        });
      }

      if (data.redirectUrl && typeof window !== "undefined") {
        const sanitizedRedirect = enrichRedirectUrl(data.redirectUrl);
        window.location.href = sanitizedRedirect ?? data.redirectUrl;
      }
    },
    [enrichRedirectUrl, isSignedIn, queryClient, redirectUrl, reset, summit],
  );

  const authenticatedRegisterMutation = useMutation({
    mutationFn: registerAuthenticatedUserForSummit,
    onSuccess: handleServerRegistrationSuccess,
    onError: (error) => {
      console.error("Authenticated registration failed:", error);
      setIsProcessing(false);
      setFlowStep("collect");
      setErrorMessage(
        getClerkErrorMessage(error, "Failed to register for summit"),
      );
    },
  });

  const unauthenticatedRegisterMutation = useMutation({
    mutationFn: registerUnauthenticatedUserForSummit,
    onSuccess: handleServerRegistrationSuccess,
    onError: (error) => {
      console.error("Summit registration failed:", error);
      setIsProcessing(false);
      setFlowStep("collect");
      setErrorMessage(
        getClerkErrorMessage(error, "Failed to register for summit"),
      );
    },
  });

  const handleAuthenticatedRegistration = () => {
    if (isProcessing || authenticatedRegisterMutation.isPending || !summit) {
      return;
    }

    setErrorMessage(null);
    setFinalRedirectUrl(undefined);
    setIsProcessing(true);
    const sanitizedRedirect = applyTrackingToRedirect(redirectUrl);
    authenticatedRegisterMutation.mutate({
      data: {
        summitId: summit.id,
        redirectUrl: sanitizedRedirect,
        utmParameters: utmParametersPayload,
      },
    });
  };

  const handleRegistrationFormSubmit = (formData: RegistrationForm) => {
    if (isProcessing || unauthenticatedRegisterMutation.isPending || !summit) {
      return;
    }

    setErrorMessage(null);
    setFinalRedirectUrl(undefined);
    setIsProcessing(true);

    const sanitizedRedirect = applyTrackingToRedirect(redirectUrl);

    unauthenticatedRegisterMutation.mutate({
      data: {
        firstName: formData.firstName,
        email: formData.email,
        summitId: summit.id,
        redirectUrl: sanitizedRedirect,
        utmParameters: utmParametersPayload,
      },
    });
  };

  const busy =
    isProcessing ||
    authenticatedRegisterMutation.isPending ||
    unauthenticatedRegisterMutation.isPending;

  if (summitLoading || (isSignedIn && registrationLoading)) {
    return (
      <div className={cn("text-center", className)}>
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <p className="mt-4 text-text-secondary">
          Loading summit information...
        </p>
      </div>
    );
  }

  const hasError =
    summitError || summitApiError || (isSignedIn && registrationError);

  if (hasError) {
    return (
      <div className={cn("text-center w-full overflow-x-hidden", className)}>
        <div className="mb-2 text-red-700">
          <AlertCircle className="mx-auto size-12" />
        </div>
        <h3 className="text-lg font-heading font-semibold">
          Something went wrong
        </h3>
        <a
          href={`mailto:info@dreamcoachmatch.com`}
          className="text-sm text-brand-700 underline hover:text-brand-600 duration-200"
        >
          Contact Support
        </a>
        <p className="mt-4 text-xs font-mono text-text-tertiary">
          {summitError?.message ||
            (isSignedIn && registrationError?.message) ||
            summitApiError ||
            "Unable to load summit information. Please try again later."}
        </p>
      </div>
    );
  }

  if (!summit) {
    return (
      <div className={cn("text-center", className)}>
        <div className="mb-4 text-text-secondary">
          <Calendar className="mx-auto h-12 w-12" />
        </div>
        <h3 className="mb-2 text-lg font-heading font-semibold">
          No Upcoming Summit
        </h3>
        <p className="text-text-secondary">
          There are no summits scheduled at the moment. Please check back later
          for updates.
        </p>
      </div>
    );
  }

  if (flowStep === "success") {
    return (
      <div className={cn("text-center", className)}>
        <div className="mb-4 text-green-500">
          <CheckCircle className="mx-auto h-12 w-12" />
        </div>
        <h3 className="mb-2 text-xl font-heading font-semibold">
          Registration Successful!
        </h3>
        <p className="mb-4 text-text-secondary">
          You're now registered for <strong>{summit.title}</strong>
        </p>
        <div className="mb-6 rounded-lg bg-gray-50 p-4">
          <div className="text-sm text-text-secondary">
            <div className="mb-1 flex items-center justify-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>
                {formatSummitDateRange(summit.startDate, summit.endDate)}
              </span>
            </div>
            <div className="text-xs">
              {new Intl.DateTimeFormat(undefined, {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }).format(new Date(summit.startDate))}{" "}
              -{" "}
              {new Intl.DateTimeFormat(undefined, {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }).format(new Date(summit.endDate))}
            </div>
          </div>
        </div>
        {finalRedirectUrl || redirectUrl ? (
          <p className="text-sm text-text-secondary">
            Redirecting you shortly...
          </p>
        ) : (
          <p className="text-sm text-text-secondary">
            We'll send you more details about the summit via email.
          </p>
        )}
      </div>
    );
  }

  if (isSignedIn && registration?.isRegistered) {
    const agendaDestination = "/summit/agenda";

    return (
      <div className={cn("text-center", className)}>
        <div className="mb-4 text-green-500">
          <CheckCircle className="mx-auto h-12 w-12" />
        </div>
        <h3 className="mb-2 text-xl font-heading font-semibold">
          You're Already Registered!
        </h3>
        <p className="mb-4 text-text-secondary">
          You're all set for <strong>{summit.title}</strong>
        </p>
        <div className="mb-6 rounded-lg bg-gray-50 p-4">
          <div className="text-sm text-text-secondary">
            <div className="mb-1 flex items-center justify-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>
                {formatSummitDateRange(summit.startDate, summit.endDate)}
              </span>
            </div>
            <div className="text-xs">
              {new Intl.DateTimeFormat(undefined, {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }).format(new Date(summit.startDate))}{" "}
              -{" "}
              {new Intl.DateTimeFormat(undefined, {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }).format(new Date(summit.endDate))}
            </div>
          </div>
        </div>
        <Button
          onClick={() => {
            if (typeof window !== "undefined") {
              window.location.href = agendaDestination;
            }
          }}
          className="w-full"
        >
          Continue
        </Button>
      </div>
    );
  }

  const renderRegistrationForm = () => (
    <form
      onSubmit={handleSubmit(handleRegistrationFormSubmit)}
      className="space-y-4"
    >
      <FormField label="First Name" error={errors.firstName?.message}>
        <Input
          {...register("firstName")}
          placeholder="First name"
          error={errors.firstName?.message}
          disabled={busy}
        />
      </FormField>
      <FormField label="Email Address" error={errors.email?.message}>
        <Input
          {...register("email")}
          type="email"
          placeholder="your@email.com"
          error={errors.email?.message}
          disabled={busy}
        />
      </FormField>
      <Button
        type="submit"
        loading={busy}
        disabled={busy}
        {...primaryCtaButtonAppearance}
      >
        {busy ? "Registering..." : "Register Now"}
      </Button>
    </form>
  );

  const renderAuthenticatedCallToAction = () => (
    <div className="space-y-4">
      <p className="text-center text-text-secondary">
        You're signed in and ready to register!
      </p>
      <Button
        onClick={handleAuthenticatedRegistration}
        loading={busy}
        disabled={busy}
        {...primaryCtaButtonAppearance}
      >
        {busy ? "Registering..." : "Register for Summit"}
      </Button>
    </div>
  );

  return (
    <div className={className} id="register">
      <div className="mt-2 mb-8 text-center">
        <h2 className="text-3xl font-heading font-bold text-balance">
          Enter Your Information Below To Register
        </h2>
      </div>

      {isSignedIn
        ? renderAuthenticatedCallToAction()
        : renderRegistrationForm()}

      {errorMessage && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}
    </div>
  );
}
